import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppContext } from '../context/AppContext';

const HistoryContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
}));

const HistoryItem = styled(ListItem)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const TruncatedText = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const ImagePreview = styled(Avatar)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: theme.shape.borderRadius,
}));

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const GenerationHistory: React.FC = () => {
  const { state, actions } = useAppContext();
  const { generationHistory } = state;

  if (generationHistory.length === 0) {
    return (
      <HistoryContainer elevation={0} variant="outlined">
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ py: 2 }}>
          No generation history yet
        </Typography>
      </HistoryContainer>
    );
  }

  const handlePlayVideo = (videoUrl: string | null) => {
    if (videoUrl) actions.loadHistoryVideo(videoUrl);
  };

  const handleDeleteHistoryItem = (id: string | null) => {
    if (!id) return;
    if (window.confirm('Are you sure you want to remove this item from history?')) {
      actions.deleteHistoryItem(id);
    }
  };

  return (
    <HistoryContainer elevation={0} variant="outlined">
      <Typography variant="h6" gutterBottom>
        Generation History
      </Typography>
      <List disablePadding>
        {generationHistory.map((item, index) => (
          <React.Fragment key={item.id || index}>
            <HistoryItem alignItems="flex-start">
              <ListItemAvatar>
                <ImagePreview src={item.image || undefined} alt="Source" variant="rounded" />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="subtitle2">{formatDate(item.timestamp)}</Typography>
                  </Box>
                }
                secondary={
                  <TruncatedText variant="body2" color="text.secondary">
                    {item.prompt}
                  </TruncatedText>
                }
                sx={{ mr: 1 }}
              />
              <Box>
                <Tooltip title="Play video">
                  <IconButton
                    edge="end"
                    aria-label="play"
                    size="small"
                    onClick={() => handlePlayVideo(item.video)}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete from history">
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() => handleDeleteHistoryItem(item.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </HistoryItem>
            {index < generationHistory.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    </HistoryContainer>
  );
};

export default GenerationHistory;
